/**
 * Helpers de formato para números, monedas, fechas.
 * Locale: es-MX (Querétaro, México).
 */

const LOCALE = 'es-MX';
const CURRENCY = 'MXN';

/** Formato moneda: $1,234.50 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: CURRENCY,
    minimumFractionDigits: 2,
  }).format(value);
}

/** Formato moneda compacto: $1.2K, $3.4M */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return '$0';
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/** Formato número con separadores: 1,234 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '0';
  return new Intl.NumberFormat(LOCALE).format(value);
}

/** Formato porcentaje: 12.5% */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/** Formato fecha corta: 15 jul 2026 */
export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleDateString(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Formato día del mes: 15 */
export function formatDay(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.getDate().toString();
}

/** Formato día de la semana: Lunes */
export function formatDayOfWeek(value: number | null | undefined): string {
  if (value == null) return '—';
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[value] ?? '—';
}

/** Mes y año: Julio 2026 */
export function formatMonthYear(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
}

/** Truncar a N caracteres con ellipsis */
export function truncate(value: string, max = 50): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

/** Formato relativo: "hace 5 min" */
export function formatRelative(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  return `hace ${diffD} d`;
}
